// Copyright 2025, University of Maryland, All Rights Reserved
interface ExecutiveSummaryData {
  overall_quality?: string;
  key_strengths?: string[];
  areas_for_improvement?: string[];
  content_overlaps?: string[] | any[];
  coverage_gaps?: string[] | any[];
}

interface ExecutiveSummaryProps {
  data: ExecutiveSummaryData;
  documentType?: string;
}

export function ExecutiveSummary({ data, documentType }: ExecutiveSummaryProps) {
  // Check if this is a syllabus document type
  const isSyllabus = documentType?.toLowerCase().includes('syllabus');
  return (
    <div className="bg-white p-8 rounded-xl shadow-lg pdf-page-break-avoid">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
        <span className="text-3xl mr-3">📋</span>
        Executive Summary
      </h2>

      <div className="mb-6 bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Overall Quality</h3>
        <p className="text-1xl font-bold text-[#E21833]">{data.overall_quality || 'High'}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">✅ Key Strengths</h3>
          <ul className="space-y-2">
            {data.key_strengths?.map((strength: string, index: number) => (
              <li key={index} className="flex items-start">
                <span className="text-[#E21833] mr-2">•</span>
                <span className="text-sm text-gray-700">{strength}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">⚠️ Areas for Improvement</h3>
          <ul className="space-y-2">
            {data.areas_for_improvement?.map((area: string, index: number) => (
              <li key={index} className="flex items-start">
                <span className="text-[#E21833] mr-2">•</span>
                <span className="text-sm text-gray-700">{area}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Content Overlaps and Coverage Gaps Section */}
      {/* Hide content overlaps for syllabi, but always show coverage gaps */}
      {((data.content_overlaps && data.content_overlaps.length > 0 && !isSyllabus) || 
        (data.coverage_gaps && data.coverage_gaps.length > 0)) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {data.content_overlaps && data.content_overlaps.length > 0 && !isSyllabus && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">🔄 Content Overlaps</h3>
              <ul className="space-y-2">
                {data.content_overlaps.map((overlap: any, index: number) => (
                  <li key={index} className="flex items-start">
                    <span className="text-[#E21833] mr-2">•</span>
                    <span className="text-sm text-gray-700">
                      {typeof overlap === 'string' ? overlap : overlap.description || JSON.stringify(overlap)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.coverage_gaps && data.coverage_gaps.length > 0 && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">🔍 Things to Note</h3>
              <ul className="space-y-2">
                {data.coverage_gaps.map((gap: any, index: number) => (
                  <li key={index} className="flex items-start">
                    <span className="text-[#E21833] mr-2">•</span>
                    <span className="text-sm text-gray-700">
                      {typeof gap === 'string' ? gap : gap.description || JSON.stringify(gap)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
